<script lang="ts">
	import { page } from '$app/stores';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import MagneticButton from '$lib/components/MagneticButton.svelte';
	import { t, type Language } from '$lib/i18n';

	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));

	const services = [
		{
			id: 'integration',
			titleKey: 'services_integration_title',
			taglineKey: 'services_integration_tagline',
			descKey: 'services_integration_desc',
			challengeKeys: [
				'services_integration_challenge_1',
				'services_integration_challenge_2',
				'services_integration_challenge_3',
				'services_integration_challenge_4'
			],
			approachKeys: [
				'services_integration_approach_1',
				'services_integration_approach_2',
				'services_integration_approach_3',
				'services_integration_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>'
		},
		{
			id: 'iot',
			titleKey: 'services_iot_title',
			taglineKey: 'services_iot_tagline',
			descKey: 'services_iot_desc',
			challengeKeys: [
				'services_iot_challenge_1',
				'services_iot_challenge_2',
				'services_iot_challenge_3',
				'services_iot_challenge_4'
			],
			approachKeys: [
				'services_iot_approach_1',
				'services_iot_approach_2',
				'services_iot_approach_3',
				'services_iot_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/></svg>'
		},
		{
			id: 'maintenance',
			titleKey: 'services_maintenance_title',
			taglineKey: 'services_maintenance_tagline',
			descKey: 'services_maintenance_desc',
			challengeKeys: [
				'services_maintenance_challenge_1',
				'services_maintenance_challenge_2',
				'services_maintenance_challenge_3',
				'services_maintenance_challenge_4'
			],
			approachKeys: [
				'services_maintenance_approach_1',
				'services_maintenance_approach_2',
				'services_maintenance_approach_3',
				'services_maintenance_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>'
		},
		{
			id: 'dashboards',
			titleKey: 'services_dashboards_title',
			taglineKey: 'services_dashboards_tagline',
			descKey: 'services_dashboards_desc',
			challengeKeys: [
				'services_dashboards_challenge_1',
				'services_dashboards_challenge_2',
				'services_dashboards_challenge_3',
				'services_dashboards_challenge_4'
			],
			approachKeys: [
				'services_dashboards_approach_1',
				'services_dashboards_approach_2',
				'services_dashboards_approach_3',
				'services_dashboards_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>'
		},
		{
			id: 'custom',
			titleKey: 'services_custom_title',
			taglineKey: 'services_custom_tagline',
			descKey: 'services_custom_desc',
			challengeKeys: [
				'services_custom_challenge_1',
				'services_custom_challenge_2',
				'services_custom_challenge_3',
				'services_custom_challenge_4'
			],
			approachKeys: [
				'services_custom_approach_1',
				'services_custom_approach_2',
				'services_custom_approach_3',
				'services_custom_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>'
		},
		{
			id: 'ai',
			titleKey: 'services_ai_title',
			taglineKey: 'services_ai_tagline',
			descKey: 'services_ai_desc',
			challengeKeys: [
				'services_ai_challenge_1',
				'services_ai_challenge_2',
				'services_ai_challenge_3',
				'services_ai_challenge_4'
			],
			approachKeys: [
				'services_ai_approach_1',
				'services_ai_approach_2',
				'services_ai_approach_3',
				'services_ai_approach_4'
			],
			icon: '<svg aria-hidden="true" class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>'
		}
	];
</script>

<svelte:head>
	<title>{tr('services_page_title')}</title>
	<meta name="description" content={tr('services_page_desc')} />
</svelte:head>

<!-- Hero + Services wrapper for seamless aurora -->
<div class="relative">
	<!-- Aurora borealis effect spanning both sections -->
	<div class="aurora-container">
		<div class="aurora-layer aurora-1"></div>
		<div class="aurora-layer aurora-2"></div>
		<div class="aurora-layer aurora-3"></div>
	</div>

	<!-- Hero -->
	<section class="relative pt-32 pb-20">
		<div
			class="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-transparent"
		></div>
		<div class="absolute inset-0 bg-grid opacity-20"></div>

		<div class="relative max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="max-w-4xl">
					<p class="text-ameta-green font-medium mb-4 tracking-wide">{tr('services_page_label')}</p>
					<h1 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
						{tr('services_page_heading')}
					</h1>
					<p class="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-3xl">
						{tr('services_page_subtitle')}
					</p>
				</div>
			</ScrollReveal>
		</div>
	</section>

	<!-- Services -->
	<section class="py-12">
		<div class="max-w-7xl mx-auto px-6">
			<div class="space-y-24">
				{#each services as service, i}
					<ScrollReveal>
						<div
							id={service.id}
							class="grid lg:grid-cols-2 gap-12 items-start scroll-mt-24 {i % 2 === 1
								? 'lg:flex-row-reverse'
								: ''}"
						>
							<div class={i % 2 === 1 ? 'lg:order-2' : ''}>
								<div
									class="w-16 h-16 rounded-2xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mb-6"
								>
									{@html service.icon}
								</div>
								<h2 class="text-3xl md:text-4xl font-bold mb-3">{tr(service.titleKey)}</h2>
								<p class="text-xl text-ameta-green mb-6">{tr(service.taglineKey)}</p>
								<p class="text-lg text-slate-400 leading-relaxed mb-8">{tr(service.descKey)}</p>

								<div class="flex flex-wrap gap-4">
									<MagneticButton
										href="/{lang}/services/{service.id}"
										class="btn-secondary inline-flex items-center gap-2"
									>
										<span>{tr('services_detail_usecases_title')}</span>
										<svg
											aria-hidden="true"
											class="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M17 8l4 4m0 0l-4 4m4-4H3"
											/>
										</svg>
									</MagneticButton>
									<MagneticButton
										href="/{lang}/#contact"
										class="btn-primary inline-flex items-center gap-2"
									>
										<span>{tr('services_page_cta_discuss')}</span>
										<svg
											aria-hidden="true"
											class="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M17 8l4 4m0 0l-4 4m4-4H3"
											/>
										</svg>
									</MagneticButton>
								</div>
							</div>

							<div class="space-y-6 {i % 2 === 1 ? 'lg:order-1' : ''}">
								<GlowCard>
									<div class="p-6">
										<h3 class="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
											{tr('services_page_challenges')}
										</h3>
										<ul class="space-y-3">
											{#each service.challengeKeys as challengeKey}
												<li class="flex items-start gap-3">
													<svg
														aria-hidden="true"
														class="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
														/>
													</svg>
													<span class="text-slate-400">{tr(challengeKey)}</span>
												</li>
											{/each}
										</ul>
									</div>
								</GlowCard>

								<GlowCard>
									<div class="p-6">
										<h3 class="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
											{tr('services_page_approach')}
										</h3>
										<ol class="space-y-3">
											{#each service.approachKeys as approachKey, j}
												<li class="flex items-start gap-3">
													<span
														class="w-6 h-6 rounded-full bg-ameta-green/20 text-ameta-green text-sm font-bold flex items-center justify-center flex-shrink-0"
													>
														{j + 1}
													</span>
													<span class="text-slate-400">{tr(approachKey)}</span>
												</li>
											{/each}
										</ol>
									</div>
								</GlowCard>
							</div>
						</div>
					</ScrollReveal>
				{/each}
			</div>
		</div>
	</section>
</div>

<!-- CTA -->
<section class="py-24 relative section-gradient">
	<div class="max-w-4xl mx-auto px-6 text-center">
		<ScrollReveal>
			<h2 class="text-3xl md:text-4xl font-bold mb-6">
				{tr('services_page_unsure_title')}
			</h2>
			<p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
				{tr('services_page_unsure_desc')}
			</p>
			<MagneticButton
				href="/{lang}/#contact"
				class="btn-primary text-lg inline-flex items-center gap-3 glow-green"
			>
				<span>{tr('services_page_unsure_cta')}</span>
				<svg
					aria-hidden="true"
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M17 8l4 4m0 0l-4 4m4-4H3"
					/>
				</svg>
			</MagneticButton>
		</ScrollReveal>
	</div>
</section>

<style>
	.aurora-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
		pointer-events: none;
		mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
		-webkit-mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
	}

	.aurora-layer {
		position: absolute;
		width: 200%;
		height: 100%;
		left: -50%;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(34, 197, 94, 0.06) 10%,
			rgba(59, 130, 246, 0.08) 30%,
			rgba(139, 92, 246, 0.06) 50%,
			rgba(34, 197, 94, 0.04) 70%,
			transparent 100%
		);
		filter: blur(80px);
		transform-origin: top center;
	}

	.aurora-1 {
		animation: aurora-wave 18s ease-in-out infinite;
		opacity: 0.35;
	}

	.aurora-2 {
		animation: aurora-wave 24s ease-in-out infinite reverse;
		opacity: 0.28;
		animation-delay: -10s;
	}

	.aurora-3 {
		animation: aurora-wave 21s ease-in-out infinite;
		opacity: 0.22;
		animation-delay: -6s;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(6, 182, 212, 0.08) 20%,
			rgba(34, 197, 94, 0.06) 40%,
			rgba(59, 130, 246, 0.04) 60%,
			transparent 100%
		);
	}

	@keyframes aurora-wave {
		0% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
		20% {
			transform: translateX(10%) skewX(8deg) scaleY(1.2) rotate(1deg);
		}
		40% {
			transform: translateX(-5%) skewX(-15deg) scaleY(0.85) rotate(-1deg);
		}
		60% {
			transform: translateX(15%) skewX(12deg) scaleY(1.15) rotate(2deg);
		}
		80% {
			transform: translateX(-10%) skewX(-8deg) scaleY(0.95) rotate(-1.5deg);
		}
		100% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
	}
</style>
